import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import axiosInstance from '../api/axios';
import { Users, Plus, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { motion, AnimatePresence } from 'framer-motion';

const Clients = () => {
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pagination, setPagination] = useState({});
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const { register, handleSubmit, reset, formState: { errors } } = useForm();

    const fetchClients = async (page = 1) => {
        setLoading(true);
        try {
            const response = await axiosInstance.get(`/clients?page=${page}`);
            setClients(response.data.data); 
            setPagination(response.data); 
        } catch (error) {
            toast.error('Error fetching clients');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchClients(); }, []);

    const onSubmit = async (data) => {
        setIsSubmitting(true);
        try {
            await axiosInstance.post('/clients', data);
            toast.success('Client added successfully! 🎉');
            reset(); 
            setIsModalOpen(false); 
            fetchClients(1); 
        } catch (error) {
            toast.error(error.response?.data?.message || 'Error saving client');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
            
            {/* Header Area */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
                <div>
                    <h2 style={{ margin: 0, fontSize: '24px', fontWeight: '700', color: '#0f172a', display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Users size={28} color="#3b82f6" /> Clients Directory
                    </h2>
                    <p style={{ margin: '5px 0 0 0', color: '#64748b', fontSize: '14px' }}>Manage your company's clients and their contact info.</p>
                </div>
                <button 
                    onClick={() => setIsModalOpen(true)} 
                    className="action-btn"
                    style={{ backgroundColor: '#3b82f6', color: 'white', padding: '12px 24px', border: 'none', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '600', boxShadow: '0 4px 6px -1px rgba(59, 130, 246, 0.3)' }}
                >
                    <Plus size={20} /> Add Client
                </button>
            </div>

            {/* Table Area */}
            <div className="glass-card" style={{ padding: '30px' }}>
                {loading ? (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>Loading data...</div>
                ) : (
                    <>
                        <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                            <thead>
                                <tr style={{ borderBottom: '2px solid #f1f5f9' }}>
                                    <th style={{ padding: '15px 0', color: '#64748b', fontWeight: '600' }}>Client Name</th>
                                    <th style={{ padding: '15px 0', color: '#64748b', fontWeight: '600' }}>Email Address</th>
                                    <th style={{ padding: '15px 0', color: '#64748b', fontWeight: '600' }}>Phone Number</th>
                                </tr>
                            </thead>
                            <tbody>
                                {clients.map((client, index) => (
                                    <motion.tr 
                                        initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: index * 0.05 }}
                                        key={client.id} style={{ borderBottom: '1px solid #f8fafc' }}
                                    >
                                        <td style={{ padding: '20px 0', fontWeight: '600', color: '#0f172a' }}>{client.name}</td>
                                        <td style={{ padding: '20px 0', color: '#475569' }}>{client.email}</td>
                                        <td style={{ padding: '20px 0', color: '#475569' }}>{client.phone || 'N/A'}</td>
                                    </motion.tr>
                                ))}
                            </tbody>
                        </table>

                        {/* Pagination */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #f1f5f9' }}>
                            <span style={{ color: '#64748b', fontSize: '14px', fontWeight: '500' }}>
                                Page {pagination.current_page} of {pagination.last_page || 1}
                            </span>
                            <div style={{ display: 'flex', gap: '10px' }}>
                                <button
                                    disabled={!pagination.prev_page_url} onClick={() => fetchClients(pagination.current_page - 1)}
                                    className="action-btn"
                                    style={{ padding: '8px 16px', border: '1px solid #e2e8f0', borderRadius: '6px', backgroundColor: 'white', color: '#0f172a', fontWeight: '500', cursor: pagination.prev_page_url ? 'pointer' : 'not-allowed', opacity: pagination.prev_page_url ? 1 : 0.5 }}
                                >
                                    Previous
                                </button>
                                <button
                                    disabled={!pagination.next_page_url} onClick={() => fetchClients(pagination.current_page + 1)}
                                    className="action-btn"
                                    style={{ padding: '8px 16px', border: '1px solid #e2e8f0', borderRadius: '6px', backgroundColor: 'white', color: '#0f172a', fontWeight: '500', cursor: pagination.next_page_url ? 'pointer' : 'not-allowed', opacity: pagination.next_page_url ? 1 : 0.5 }}
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    </>
                )}
            </div>

            {/* Modal Area */}
            <AnimatePresence>
                {isModalOpen && (
                    <motion.div 
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(15, 23, 42, 0.6)', backdropFilter: 'blur(4px)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 50 }}
                    >
                        <motion.div 
                            initial={{ scale: 0.95, opacity: 0, y: 20 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.95, opacity: 0, y: 20 }}
                            className="glass-card" style={{ padding: '30px', width: '400px', maxWidth: '90%' }}
                        >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px' }}>
                                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700' }}>Add New Client</h3>
                                <button onClick={() => setIsModalOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }}><X size={24} /></button>
                            </div>

                            <form onSubmit={handleSubmit(onSubmit)} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                                <div>
                                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#475569' }}>Company / Client Name</label>
                                    <input {...register('name', { required: 'Name is required' })} style={{ width: '100%', padding: '12px', border: '1px solid #e2e8f0', borderRadius: '8px', outline: 'none' }} />
                                </div>
                                <div>
                                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#475569' }}>Email Address</label>
                                    <input type="email" {...register('email', { required: 'Email is required' })} style={{ width: '100%', padding: '12px', border: '1px solid #e2e8f0', borderRadius: '8px', outline: 'none' }} />
                                </div>
                                <div>
                                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#475569' }}>Phone Number</label>
                                    <input {...register('phone')} style={{ width: '100%', padding: '12px', border: '1px solid #e2e8f0', borderRadius: '8px', outline: 'none' }} />
                                </div>

                                <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                                    <button type="submit" disabled={isSubmitting} className="action-btn" style={{ flex: 1, padding: '12px', backgroundColor: '#10b981', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}>
                                        {isSubmitting ? 'Saving...' : 'Save Client'}
                                    </button>
                                </div>
                            </form>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    );
};

export default Clients;